'use client'

import { useEffect } from 'react'
import { useTranslations } from 'next-intl'

// Direct error reporting for global errors (can't use hooks/context here)
function reportGlobalError(error: Error & { digest?: string }) {
  // React 19 attaches componentStack to error objects during rendering
  const componentStack = (error as Error & { componentStack?: string }).componentStack
  fetch('/api/errors', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      message: error.message,
      stack: error.stack,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      componentStack: componentStack || undefined,
      digest: error.digest,
      metadata: {
        type: 'error_boundary',
        boundary: 'global',
        severity: 'critical',
      },
    }),
  }).catch(() => {
    // Silently fail - don't cause more errors
  })
}

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const t = useTranslations('errors.critical')

  useEffect(() => {
    // Log the error to our error reporting service
    console.error('Global application error:', error)
    reportGlobalError(error)
  }, [error])

  return (
    <html lang="en">
      <body className="bg-zinc-900 text-white">
        <div className="min-h-screen flex flex-col items-center justify-center p-4">
          <div className="text-center max-w-md">
            {/* Logo */}
            <div className="mb-8">
              <div className="text-2xl font-bold">
                iPerform<span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-500 to-fuchsia-500">.live</span>
              </div>
            </div>

            {/* Error Icon */}
            <div className="mb-6">
              <div className="p-4 rounded-full bg-red-500/10 inline-block">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-12 w-12 text-red-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
            </div>

            {/* Error Message */}
            <h1 className="text-3xl font-bold text-white mb-2">{t('title')}</h1>
            <p className="text-zinc-400 mb-6">
              {t('description')}
            </p>

            {/* Error Details (in development) */}
            {process.env.NODE_ENV === 'development' && (
              <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-left">
                <p className="text-sm font-mono text-red-400 break-all">
                  {error.message}
                </p>
                {error.digest && (
                  <p className="text-xs text-zinc-500 mt-2">
                    {t('errorId', { digest: error.digest })}
                  </p>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={reset}
                className="px-4 py-2 rounded-lg border border-zinc-700 text-white hover:bg-zinc-800 transition-colors"
              >
                {t('tryAgain')}
              </button>
              {/* eslint-disable-next-line @next/next/no-html-link-for-pages -- Full page refresh is safer in global error boundary */}
              <a
                href="/"
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white hover:from-violet-700 hover:to-fuchsia-700 transition-colors"
              >
                {t('goHome')}
              </a>
            </div>
          </div>
        </div>
      </body>
    </html>
  )
}
